import React, { useEffect, useRef, useState } from "react";

import {
  Calendar,
  LayoutDashboard,
  MessageSquare,
  Search,
  Stethoscope,
  Undo2,
  UserCheck,
  Users,
  type LucideIcon,
} from "lucide-react";

import type EncounterPatientSerializer from "issara-sdk/types/EncounterPatientSerializer_core";
import type EncounterSerializer from "issara-sdk/types/EncounterSerializer_core";
import type PatientSerializer from "issara-sdk/types/PatientSerializer_apps_REG";

import { BLC_STATUS } from "react-lib/apps/HISV3/common/CommonInterface";
import { type State as OrderCentralLabState } from "react-lib/apps/HISV3/LAB/sequence/OrderCentralLab";
import { type OrderSetTemplateSequence } from "react-lib/apps/HISV3/common/sequence/OrderSetTemplate";
import type { WSMessenger } from "react-lib/compat/vc-websocket";

import { AlertV4, TabsV4, type TabsV4Item } from "./common/ui";
import CommunicationNoteModalV4 from "./common/CommunicationNoteModalV4";
import TopBarV4 from "./TopBarV4";
import ViewEncounterV4, { isEncounterRecord, type EncounterRecord } from "./ViewEncounterV4";
import CardPatientListV4, { type QueueFilterState } from "./common/CardPatientListV4";
import {
  EXAM_V4_CARDS,
  PATIENT_LIST_V4_ACTIONS,
  PATIENT_LIST_V4_CARD,
  PATIENT_LIST_V4_SEQUENCE,
  stageOf,
  type QueueEncounter,
} from "./common/commonV4Interface";
/* practice ของทีม (PatientOrdersV4): ชื่อ sequence + ACTIONS มาจากไฟล์ sequence */
import { ACTIONS as EXAM_ACTIONS, SEQUENCE_NAME as EXAM_SEQUENCE } from "./common/sequence/ExamV4";
import {
  ACTIONS as CONSULT_ACTIONS,
  CARD_NAME as CONSULT_CARD_NAME,
  SEQUENCE_NAME as CONSULT_SEQUENCE,
} from "./common/sequence/MultidisciplinaryV4";
import {
  ACTIONS as RESULT_ACTIONS,
  CARD_NAME as RESULT_CARD_NAME,
  SEQUENCE_NAME as RESULT_SEQUENCE,
} from "./common/sequence/ResultSummaryV4";
import {
  ACTIONS as HISTORY_ACTIONS,
  CARD_NAME as HISTORY_CARD_NAME,
  SEQUENCE_NAME as HISTORY_SEQUENCE,
} from "./common/sequence/PatientHistoryV4";
import {
  ACTIONS as REGISTRATION_ACTIONS,
  BTN_ACTS as REGISTRATION_BTN_ACTS,
} from "./common/sequence/RegistrationV4";
import PatientPanelV4, { patientMenuTitle, type PatientMenuKey } from "./patient/PatientPanelV4";
import PresenceBarV4 from "./presence/PresenceBarV4";
import { makeEncounterPageKey } from "./presence/presenceKeysV4";
import { usePresenceV4 } from "./presence/usePresenceV4";
import BillingHistoryTabV4 from "./patient/BillingHistoryTabV4";
import BillingWalletTabV4 from "./patient/BillingWalletTabV4";
import BillingPendingTabV4 from "./patient/BillingPendingTabV4";
import CardBillingV4, { type BillingTabKey } from "./patient/CardBillingV4";
import CardDoctorV4 from "./patient/CardDoctorV4";
import CardGalleryV4 from "./patient/CardGalleryV4";
import CardNursingV4 from "./patient/CardNursingV4";
import CardPatientFlagV4 from "./patient/CardPatientFlagV4";
import CardOrdersV4 from "./patient/CardOrdersV4";
import CardResultSummaryV4 from "./patient/CardResultSummaryV4";
import CardTeleHealthV4, { type TeleChatDetail } from "./patient/CardTeleHealthV4";
import CardPackageV4 from "./patient/CardPackageV4";
import CardPatientHistoryV4 from "./patient/CardPatientHistoryV4";
import CardDocumentV4 from "./patient/CardDocumentV4";
import CardScreenV4 from "./patient/CardScreenV4";
import {
  asNumber,
  asText,
  doctorIdOf,
  emrIdOf,
  emrOfEncounter,
  isReceiptCreated,
  normalizeExamV4Slice,
  type ExamIds,
} from "./patient/examModelV4";
import { isDoctorRole } from "./patient/examDispatchV4";
import { profileProblemMessagesOf } from "./patient/screenModelV4";
import {
  DjangoState,
  ExamV4SequenceState,
  PatientFlagState,
  PatientListV4SequenceState,
  MultidisciplinaryV4SequenceState,
  OrderSheetV4SequenceState,
  OrderAppointmentV4SequenceState,
  PatientOrdersV4SequenceState,
  ResultSummaryV4SequenceState,
  SelectedEmrState,
  PatientHistoryV4SequenceState,
  DocumentV4SequenceState,
  DOCUMENT_ACTIONS,
  DOCUMENT_V4_CARD,
  DOCUMENT_V4_SEQUENCE,
  ScreenV4SequenceState,
  SCREEN_ACTIONS,
  SCREEN_V4_CARD,
  SCREEN_V4_SEQUENCE,
  V3Pages,
} from "./MainHISV4Interface";
import CardSearchMemberV4, { type OpenPatientTarget } from "./registration/CardSearchMemberV4";
import ViewRegistrationV4 from "./registration/ViewRegistrationV4";
import CardOpenEncounterV4 from "./registration/CardOpenEncounterV4";
import { POS_MENU, type RegistrationMenuKey } from "./registration/registrationModelV4";
import CardPurchaseOrdersV4 from "./purchase/CardPurchaseOrdersV4";
import CardAppointmentV4, { type CardAppointmentV4Props } from "./appointment/CardAppointmentV4";
import CardChatV4, { type CardChatV4Handle, type CardChatV4Props } from "./chat/CardChatV4";
import type {
  RegistrationV4SequenceState,
  TeleHealthV4SequenceState,
  PackageV4SequenceState,
  BillingV4SequenceState,
  BillingHistoryV4SequenceState,
  BillingWalletV4SequenceState,
} from "./common/commonV4Interface";
import "./HISV4.scss";
import "./HISV4Patient.scss";

export interface MainHISV4Props {
  initialTab?: string;
  /**
   * messenger ตัวที่ MainHIS ต่อไว้กับห้อง screen ของ division (`vcMessengerMSG.connect("MSG", {screens: division.id})`)
   * presence ใช้ห้องเดียวกันนี้รับ delta "presence_update" — ไม่ส่ง = client ใช้ vcMessenger singleton
   * ของโมดูลซึ่ง HIS ไม่ได้ต่อห้องไว้ -> จะได้แค่ polling
   */
  vcMessengerMSG?: WSMessenger;
  userProfile?: any;
  django?: DjangoState;
  setProp?: (key: string, value: any) => void;
  onEvent?: (event: any) => void;
  selectedLanguage?: "en" | "th";
  onChangeSelectedLanguage?: (e: any, data: any) => void;
  onBackToMainHIS?: () => void;
  divisionOptions?: any[];
  selectedDivision?: any;
  deviceOptions?: any[];
  selectedDevice?: any;
  showIconNoti?: boolean;
  showTabName?: string;
  billingLockNotificationList?: any[];
  billLockNotiMessageList?: any[];
  rejectOrderNotificationList?: any[];
  tabPatientRef?: any;
  mode?: string;
  languageOptions?: any;
  languageUX?: "en" | "th";
  isSocialLogin?: boolean;
  BillingAndPackageSequence?: any;
  BillingCreditWallet?: any;
  BillingCarts?: any;
  BillPaymentSequence?: any;
  controller?: any;
  runSequence?: any;
  PatientListV4Sequence?: PatientListV4SequenceState;
  ExamV4Sequence?: ExamV4SequenceState | null;
  selectedEmr?: SelectedEmrState | null;
  PatientOrdersV4Sequence?: PatientOrdersV4SequenceState;
  /** บันทึกทีมสหสาขา (MultidisciplinaryV4Sequence) */
  MultidisciplinaryV4Sequence?: MultidisciplinaryV4SequenceState | null;
  OrderCentralLabSequence?: OrderCentralLabState["OrderCentralLabSequence"];
  OrderSetTemplateSequence?: OrderSetTemplateSequence;
  OrderSheetV4Sequence?: OrderSheetV4SequenceState;
  OrderAppointmentV4Sequence?: OrderAppointmentV4SequenceState;
  ResultSummaryV4Sequence?: ResultSummaryV4SequenceState | null;
  /** รายการ encounter ย้อนหลังของหน้า Patient History Viewer (4d) */
  PatientHistoryV4Sequence?: PatientHistoryV4SequenceState | null;
  /** state ของ sequence เอกสาร/ใบรับรองแพทย์ (DocumentV4Sequence) */
  DocumentV4Sequence?: DocumentV4SequenceState | null;
  /** state ของ sequence คัดกรองผู้ป่วย (ScreenV4Sequence) */
  ScreenV4Sequence?: ScreenV4SequenceState | null;
  currentDoctor?: Record<string, any>;

  buttonLoadCheck?: Record<string, any>;
  errorMessage?: Record<string, any>;
  tabAdminRef?: any;
  onClickBell?: any;
  selectedPatient?: PatientSerializer | null;
  selectedEncounter?: EncounterSerializer | null;
  patientADR?: PatientFlagState | null;
  problemListDPO?: PatientFlagState | null;
  v3Pages?: V3Pages;
  // Search Member / หน้าผู้ป่วย (sequence V3 SetPatient, CreateUpdateEncounter, MeBilling)
  SetPatientSequence?: any;
  RegistrationV4Sequence?: RegistrationV4SequenceState;
  TeleHealthV4Sequence?: TeleHealthV4SequenceState;
  PackageV4Sequence?: PackageV4SequenceState;
  BillingV4Sequence?: BillingV4SequenceState;
  BillingHistoryV4Sequence?: BillingHistoryV4SequenceState;
  BillingWalletV4Sequence?: BillingWalletV4SequenceState;
  CreateUpdateEncounterSequence?: any;
  appointmentEncounterList?: any[];
  selectedAppointmentItems?: number[];
  rehabAppointmentDoctors?: any[] | null;
  MeBillingSequence?: any;
  masterOptions?: Record<string, any>;
  masterData?: Record<string, any>;
  /** controller เดิมของ V3 สำหรับ Print list หน้า Document */
  printListController?: Record<string, (...args: any[]) => Promise<any[]>>;
  successMessage?: Record<string, any>;
  // นัดหมาย
  AppointmentListMEHSequence?: CardAppointmentV4Props["AppointmentListMEHSequence"];
  MakeAppointmentMeHeySequence?: CardAppointmentV4Props["MakeAppointmentMeHeySequence"];
  searchedItemListWithKey?: Record<string, unknown>;
  // แชท
  chatRef?: React.Ref<CardChatV4Handle>;
  chatProps?: Omit<CardChatV4Props, "onGoAppointments">;
  // TeleHealth (ห้องแชทของผู้ป่วยที่ SelectEncounter โหลดไว้)
  chatDetail?: TeleChatDetail | null;
  classifyController?: any;
  diagFormController?: any;
}

/** stage ของคิวที่ต้อง "เรียกเข้าตรวจ" ก่อน (QUEUE_STAGES ใน common/sequence/PatientListV4.ts) */
const CALL_IN_STAGE_KEY = "wait-doctor";

type NavKey = "patient" | "queue" | "appointment" | "chat" | "dashboard";
type PatientTab = "member" | "queue";
type QueueTab = "patientQ" | "orders";
type DashboardTab = "services" | "report";

/** เมนูหลักใน rail ซ้าย (บนลงล่าง) */
const RAIL: { key: NavKey; label: string; Icon: LucideIcon }[] = [
  { key: "patient", label: "Patient", Icon: Users },
  { key: "queue", label: "คิววันนี้", Icon: Stethoscope },
  { key: "appointment", label: "นัดหมาย", Icon: Calendar },
  { key: "chat", label: "แชท", Icon: MessageSquare },
  { key: "dashboard", label: "Dashboard", Icon: LayoutDashboard },
];

const PATIENT_TABS: TabsV4Item<PatientTab>[] = [
  { key: "member", label: "Search Member" },
  { key: "queue", label: "Patient Queue" },
];
const QUEUE_TABS: TabsV4Item<QueueTab>[] = [
  { key: "patientQ", label: "Patient Queue" },
  { key: "orders", label: "รายการสั่งซื้อ" },
];
const DASHBOARD_TABS: TabsV4Item<DashboardTab>[] = [
  { key: "services", label: "Services Queue" },
  { key: "report", label: "Report" },
];

export const MainHISV4: React.FC<MainHISV4Props> = (props) => {
  const {
    initialTab = "queue",
    userProfile = {
      name: "ทีมคลินิก (ล็อคอินรายบุคคล)",
      role: "TH",
      language: "TH",
    },
    django,
    setProp,
    onEvent,
    selectedLanguage = "th",
    onChangeSelectedLanguage,
    onBackToMainHIS,
  } = props;
  const [activeNav, setActiveNav] = useState<string>(initialTab);
  const [patientTab, setPatientTab] = useState<PatientTab>("member");
  const [queueTab, setQueueTab] = useState<QueueTab>("patientQ");
  const [dashboardTab, setDashboardTab] = useState<DashboardTab>("services");
  const [selectedEncounter, setSelectedEncounter] = useState<EncounterPatientSerializer | null>(
    null,
  );
  const [patientMenu, setPatientMenu] = useState<PatientMenuKey>("doctor");
  /** editor ของประวัติแพ้ / โรคประจำตัว (live mode เท่านั้น) */
  const [flagEditorOpen, setFlagEditorOpen] = useState(false);
  /** หน้าผู้ป่วยจาก Search Member (null = หน้าค้นหา) */
  const [memberMenu, setMemberMenu] = useState<RegistrationMenuKey | null>(null);
  const [memberHistoryPatientId, setMemberHistoryPatientId] = useState<number | undefined>();
  /** เมนูย่อยของหน้า POS (V3 CardMeBillingMenuContent) */
  const [posMenu, setPosMenu] = useState<string>(POS_MENU);
  /** ตัวกรองของคิววันนี้ คงไว้ตอนกลับจากหน้าผู้ป่วย */
  const [queueFilter, setQueueFilter] = useState<QueueFilterState>();
  /** #86727: แถวที่กดปุ่ม Communication Note (null = ไม่เปิด pop-up — ร่าง note เก็บใน modal itself) */
  const [noteModalRow, setNoteModalRow] = useState<QueueEncounter | null>(null);

  /* slice ของ ExamV4 (อาจเป็น Partial ตอน framework เขียนชั่วคราว) -> normalize ก่อนอ่าน */
  const examView = props.ExamV4Sequence ? normalizeExamV4Slice(props.ExamV4Sequence) : null;

  /**
   * flag "ประวัติแพ้" / "โรคประจำตัว" ของ Patient Panel:
   * ถ้า parent (MainHIS ของ V3) โหลดไว้ให้ จะใช้ของ parent ก่อน
   * ถ้ายังไม่มี จะใช้ชุดที่ ExamV4 โหลดมาพร้อมเวชระเบียน
   * PatientPanelV4 คาด shape { items: [...] } ตาม API (AdverseReactionList / ProblemListList)
   */
  /* ประวัติสุขภาพประจำตัวเพิ่งบันทึกในหน้าคัดกรอง -> flag ของ Patient Panel ต้องขึ้นทันที
     (issue 86615 ข้อ 8) โดยไม่รอให parent หรือ ExamV4 โหลดชุดใหม่ */
  const screeningRows = (): { problems: any[]; adrs: any[] } => {
    const profile = props.ScreenV4Sequence?.healthProfile;
    if (!profile) return { problems: [], adrs: [] };

    const problems = profileProblemMessagesOf(profile).map((message, index) => ({
      id: -(index + 1),
      message,
      is_active: true,
    }));

    const adrs: any[] = (profile.allergies || []).map((item, index) => ({
      id: item.id ?? -(index + 1),
      name: item.name,
      note: item.react && item.react !== "-" ? item.react : "",
      status: "NOTED",
      type_name: item.type,
    }));
    if (profile.nkda && adrs.length === 0) {
      adrs.push({ id: -1, name: "", note: "", status: "NO_REACTION", type_name: "อื่นๆ" });
    }

    return { problems, adrs };
  };

  const mergeFlagState = (base: any, extra: any[]): unknown => {
    if (extra.length === 0) return base === null || base === undefined ? undefined : base;
    const items = [...(((base as any) && (base as any).items) || []), ...extra];
    return { ...((base as any) || {}), items };
  };

  const patientADRState = (): unknown => {
    const fromParent =
      props.patientADR !== undefined && props.patientADR !== null
        ? props.patientADR
        : examView === null
          ? null
          : { items: examView.allergyItems };
    return mergeFlagState(fromParent, screeningRows().adrs);
  };
  const problemListState = (): unknown => {
    const screen = props.ScreenV4Sequence;
    const currentPatient = asNumber(selectedEncounter?.patient_id);
    const screenReady = currentPatient !== undefined && asNumber(screen?.patientId) === currentPatient
      && Array.isArray(screen?.problemRows);
    const examReady = currentPatient !== undefined && examView?.ids.patientId === currentPatient;
    if (screenReady && (!examReady || !examView?.flagsLoadedAt
      || (screen?.problemRowsUpdatedAt ?? "") >= examView.flagsLoadedAt)) {
      return { items: screen!.problemRows };
    }
    if (examReady && examView?.flagsLoadedAt) return { items: examView.problemItems };
    const fromParent =
      props.problemListDPO !== undefined && props.problemListDPO !== null
        ? props.problemListDPO
        : examView === null
          ? null
          : { items: examView.problemItems };
    return mergeFlagState(fromParent, screeningRows().problems);
  };

  useEffect(() => {
    const screen = props.ScreenV4Sequence;
    if (!screen?.panelUpdatedAt || !selectedEncounter?.patient_id
      || asNumber(screen.encounterId) !== asNumber(selectedEncounter.id)) return;
    props.runSequence({ sequence: EXAM_SEQUENCE, action: EXAM_ACTIONS.LOAD_PANEL,
      card: EXAM_V4_CARDS.panel, ...examIds() });
  }, [props.ScreenV4Sequence?.panelUpdatedAt]);

  /**
   * คำสั่งแพทย์ของ encounter (จากคิว) ใช้กับช่อง ผูกบันทึกกับคำสั่ง (doctor_order_id)
   * V3 ส่ง doctor_order_id ใน create/update nurse note (`PTMI.ts:808-810`)
   */
  const nurseDoctorOrders = (): { id: number; label: string }[] =>
    (
      (props.selectedEncounter?.doctor_orders ?? []) as {
        id?: unknown;
        specific_type?: string;
        order_status?: string;
      }[]
    )
      .map((order) => ({
        id: asNumber(order.id),
        label: [order.specific_type, order.order_status].filter(Boolean).join(" · ") || "",
      }))
      .filter(
        (order): order is { id: number; label: string } =>
          order.id !== null && order.id !== undefined,
      );

  /* selectedPatient / selectedEncounter ถูกเขียนทับได้จากหน้าอื่น (Search Member, เปิด Encounter) */
  const isLoaded =
    !!selectedEncounter?.patient_id &&
    props.selectedEncounter?.id === selectedEncounter.id &&
    asNumber(props.selectedPatient?.id) === asNumber(selectedEncounter.patient_id);
  const patient = isLoaded ? props.selectedPatient : null;
  const encounter = isLoaded ? props.selectedEncounter : selectedEncounter;

  /* encounter ที่เปิดค้างไว้เคยโหลดครบแล้วถูกเขียนทับ: โหลดผู้ป่วย / encounter กลับเมื่อกลับมาหน้าคิว */
  const loadedEncounterId = useRef<unknown>(null);
  useEffect(() => {
    if (isLoaded) {
      loadedEncounterId.current = selectedEncounter?.id;
      return;
    }
    if (activeNav !== "queue" || !selectedEncounter?.patient_id) return;
    if (loadedEncounterId.current !== selectedEncounter.id) return;
    loadedEncounterId.current = null;
    onEvent?.({ message: "SelectEncounter", params: { encounter: selectedEncounter } });
  }, [isLoaded, activeNav, selectedEncounter?.id]);

  /** selectedEmr ค้างเป็นของผู้ป่วยคนก่อนได้จนกว่า SelectEncounter จะโหลดเสร็จ ต้องกรองก่อนใช้เสมอ */
  const emrOf = (target?: { id?: unknown } | null): typeof props.selectedEmr =>
    emrOfEncounter(props.selectedEmr, target);

  /** id ที่หน้าตรวจรักษาต้องใช้ (emrId ยังไม่มี -> ExamV4.Start จะ resolve เอง)
      doctorId เอาจาก encounter ก่อน (approve_by / doctor) แบบเดียวกับ V3 แล้วค่อย emr */
  const examIds = (): ExamIds => ({
    encounterId: asNumber(selectedEncounter?.id),
    patientId: asNumber(selectedEncounter?.patient_id),
    emrId: emrIdOf(emrOf(selectedEncounter), selectedEncounter) ?? undefined,
    doctorId: doctorIdOf(encounter, emrOf(selectedEncounter)),
  });

  /* ------------------------------------------------------------------ presence
     แจ้งเตือน "หน้านี้มีผู้อื่นกำลังใช้งานอยู่" ของ HIS V4 เท่านั้น
     page_key = <division>/HISV4/<menu>/enc:<encounterId>  (แยกเช็คต่อ encounter ต่อ menu)

     ไม่มี flag ใดในฝั่ง client (ไม่ต้องแก้ base.json/config.ts ของ repo หลัก):
     master switch อยู่ที่ constance core_PRESENCE_ENABLED ของ backend —
     เมื่อปิดอยู่ join ตอบ {"enabled": false} แล้ว PresenceClientV4 หยุดเรียกทุก endpoint
     ไม่เลือก encounter / mock mode (ไม่มี division) = pageKey null -> ไม่เรียก API เลย */
  const presenceDivisionId = (): number | undefined =>
    asNumber(props.selectedDivision?.id) ?? asNumber(django?.division) ?? undefined;

  const presencePageKey =
    selectedEncounter === null
      ? null
      : makeEncounterPageKey(presenceDivisionId(), patientMenu, asNumber(selectedEncounter?.id));

  const presence = usePresenceV4({
    pageKey: presencePageKey,
    divisionId: presenceDivisionId(),
    apiToken: props.controller?.apiToken ?? null,
    messenger: props.vcMessengerMSG,
  });

  const presenceBinding =
    presencePageKey === null
      ? undefined
      : {
          bar: <PresenceBarV4 peers={presence.peers} channel={presence.channel} />,
          setActive: presence.setActive,
          setTyping: presence.setTyping,
        };

  /** สิทธิ์ "เรียกผู้ป่วยเข้าตรวจ" = ตามที่ backend อนุญาตบน Encounter นั้นจริง
      (allowed_actions มี OPD_CHECK_IN — same source ที่ใช้ใน pre-check การคัดกรอง #86747)
      V3 gate ด้วยบทบาทแพทย์ (CardPatientListInterface.ts:854) แต่หน้าจออื่นของ HIS ปล่อยตามสิทธิ์
      และผู้ใช้ที่ไม่ใช่ role DOCTOR ก็เป็นแพทย์เจ้าของ encounter ได้ เช่น นักเทคนิคการแพทย์
      (ยืนยันจาก local: PUT /apis/DPO/encounter-opd-check-in/9850/ ตอบ 200 -> IN_EXAM)
      ถ้าแถว/encounter ไม่มี allowed_actions (โหลดไม่ครบ) fallback ไปบทบาทแพทย์เหมือน V3 */
  const callInAllowedFor = (row?: { allowed_actions?: unknown } | null): boolean => {
    const raw = row === null || row === undefined ? undefined : row.allowed_actions;
    const actions = Array.isArray(raw) ? raw.map((item) => `${item}`) : null;
    if (actions !== null) return actions.indexOf("OPD_CHECK_IN") !== -1;
    const roleTypes = django?.user?.role_types;
    return roleTypes === undefined ? true : isDoctorRole(roleTypes);
  };
  /** บทบาทแพทย์เท่านั้น (ใช้กับงานของแพทย์จริง ๆ เช่น การออกใบรับรอง) */
  const isDoctorUser = (): boolean => {
    const roleTypes = django?.user?.role_types;
    return roleTypes === undefined ? true : isDoctorRole(roleTypes);
  };
  const canCallInExam = (): boolean => callInAllowedFor(encounter ?? null);

  /** สถานะ encounter (raw จาก API + ชื่อไทยสำหรับแสดง) */
  const encounterStatusOf = (): { status: string; statusName: string } => {
    const raw = props.selectedEncounter?.status ?? selectedEncounter?.status ?? "";
    const stage = stageOf({ status: raw });
    return {
      status: String(raw ?? ""),
      statusName: String(props.selectedEncounter?.status_name ?? stage?.label ?? ""),
    };
  };

  /* ผู้ป่วยที่เลือกจาก Search Member (SetPatient.select เขียน SetPatientSequence.patient) */
  const memberPatientId =
    memberMenu === "history" && memberHistoryPatientId !== undefined
      ? memberHistoryPatientId
      : asNumber(props.SetPatientSequence?.patient?.id);
  const memberDivisionId = asNumber(props.selectedDivision?.id);
  const memberWalkIn = props.RegistrationV4Sequence?.walkIn?.patientId === memberPatientId
    && props.RegistrationV4Sequence?.walkIn?.divisionId === memberDivisionId
      ? props.RegistrationV4Sequence?.walkIn : undefined;
  const otherRegistrationMenu = activeNav === "patient" && patientTab === "member"
    && memberMenu !== null && memberMenu !== "encounter";
  useEffect(() => {
    if (!otherRegistrationMenu || memberPatientId === undefined || memberDivisionId === undefined || memberWalkIn) return;
    props.runSequence({ sequence: "RegistrationV4", action: REGISTRATION_ACTIONS.LOAD_WALKIN, patientId: memberPatientId });
  }, [otherRegistrationMenu, memberPatientId, memberDivisionId, memberWalkIn]);

  const memberCustomerReady =
    memberPatientId !== undefined &&
    asNumber(props.MeBillingSequence?.selectedCustomer?.patientDetail?.id) === memberPatientId;

  useEffect(() => {
    if (memberMenu === null || memberPatientId === undefined) return;
    props.runSequence({
      sequence: "RegistrationV4",
      action: REGISTRATION_ACTIONS.LOAD_TIERS,
      patientIds: [memberPatientId],
    });
  }, [memberMenu !== null, memberPatientId]);

  useEffect(() => {
    if (memberMenu !== "pos" || memberPatientId === undefined || memberCustomerReady) return;
    props.runSequence({
      sequence: "RegistrationV4",
      action: REGISTRATION_ACTIONS.LOAD_CUSTOMER,
      patientId: memberPatientId,
    });
  }, [memberMenu, memberPatientId]);

  /* รับชำระแล้วสมาชิก/คะแนนเปลี่ยนได้ -> โหลดระดับสมาชิกใหม่ */
  useEffect(() => {
    if (!isReceiptCreated(props.BillPaymentSequence)) return;
    if (memberMenu !== null && memberPatientId !== undefined) {
      props.runSequence({
        sequence: "RegistrationV4",
        action: REGISTRATION_ACTIONS.LOAD_TIERS,
        patientIds: [memberPatientId],
        refresh: true,
      });
    }
    /* รับชำระจาก Search Member ระหว่างที่ encounter ยังเปิดค้างอยู่ (isLoaded เป็น false ชั่วคราว) */
    if (selectedEncounter?.patient_id) {
      props.runSequence({
        sequence: EXAM_SEQUENCE,
        action: EXAM_ACTIONS.LOAD_PANEL,
        card: EXAM_V4_CARDS.panel,
        ...examIds(),
      });
    }
  }, [props.BillPaymentSequence]);

  /* callIn: ปุ่ม "เรียกเข้าตรวจ" ของคิว ทำใน dispatch เดียวกับการเปิดหน้า
     ถ้ายิงแยกกัน LOAD(restart) กับ START_EXAM จะแข่งกันเขียน slice */
  const openEncounter = (encounter: unknown, callIn: boolean): void => {
    const record = (isEncounterRecord(encounter) ? encounter : {}) as EncounterPatientSerializer;
    setSelectedEncounter(record);
    const menu = stageOf(record)?.menu ?? "doctor";
    setPatientMenu(menu);
    if (!record.patient_id) return;
    onEvent?.({
      message: "SelectEncounter",
      params: { encounter: record },
    });
    /* โหลดข้อมูลตรวจรักษา + gallery + บันทึกการพยาบาล ของ encounter นี้ */
    props.runSequence({
      sequence: EXAM_SEQUENCE,
      action: callIn ? EXAM_ACTIONS.START_EXAM : EXAM_ACTIONS.LOAD,
      card: EXAM_V4_CARDS.doctor,
      restart: true,
      encounterId: asNumber(record.id),
      patientId: asNumber(record.patient_id),
      emrId: emrIdOf(emrOf(record), record) ?? undefined,
      doctorId: doctorIdOf(record, emrOf(record)),
      /* ส่งสถานะติดไปกับ action เสมอ: ExamV4 ใช้ตัดสิน auto check-in IN_QUEUE -> IN_EXAM (V3) */
      currentStatus: String(record.status ?? ""),
    });
    if (patientMenu === "docs" || patientMenu === "medcert") {
      props.runSequence({
        sequence: DOCUMENT_V4_SEQUENCE,
        action: DOCUMENT_ACTIONS.LOAD,
        card: DOCUMENT_V4_CARD,
        restart: true,
        encounterId: asNumber(record.id),
        patientId: asNumber(record.patient_id),
        emrId: emrIdOf(emrOf(record), record) ?? undefined,
        doctorId: doctorIdOf(record, emrOf(record)),
        patientHn: String(record.hn || ""),
      });
    }
    if (patientMenu === "screen" || menu === "screen") {
      props.runSequence({
        sequence: SCREEN_V4_SEQUENCE,
        action: SCREEN_ACTIONS.LOAD,
        card: SCREEN_V4_CARD,
        restart: true,
        encounterId: asNumber(record.id),
        patientId: asNumber(record.patient_id),
        doctorId: doctorIdOf(record, emrOf(record)),
        patientHn: String(record.hn || ""),
      });
    }
  };

  const handleSelectMenu = (menu: PatientMenuKey): void => {
    setPatientMenu(menu);
    if (!selectedEncounter) return;
    if (menu === "gallery") {
      props.runSequence({
        sequence: EXAM_SEQUENCE,
        action: EXAM_ACTIONS.LOAD_GALLERY,
        card: EXAM_V4_CARDS.gallery,
        ...examIds(),
      });
    }
    if (menu === "nursing") {
      props.runSequence({
        sequence: EXAM_SEQUENCE,
        action: EXAM_ACTIONS.LOAD_NURSE,
        card: EXAM_V4_CARDS.nursing,
        ...examIds(),
      });
      /*Multidisciplinary เป็นอีก sequenceหนึ่ง (data คนละชุดกันกับ nurse note) */
      props.runSequence({
        sequence: CONSULT_SEQUENCE,
        action: CONSULT_ACTIONS.LOAD,
        card: CONSULT_CARD_NAME,
        ...examIds(),
      });
    }
    if (menu === "history") {
      /* หน้า PHV อ่านประวัติย้อนหลังของ patient ทั้งคน (ไม่ผูกกับ encounter เดียว) */
      props.runSequence({
        sequence: HISTORY_SEQUENCE,
        action: HISTORY_ACTIONS.LOAD,
        card: HISTORY_CARD_NAME,
        restart: true,
        patientId: asNumber(selectedEncounter?.patient_id),
        encounterId: asNumber(selectedEncounter?.id),
      });
    }
    if (menu === "results") {
      props.runSequence({
        sequence: RESULT_SEQUENCE,
        action: RESULT_ACTIONS.LOAD,
        card: RESULT_CARD_NAME,
        restart: true,
        encounterId: asNumber(selectedEncounter?.id),
        patientId: asNumber(selectedEncounter?.patient_id),
        emrId: emrIdOf(emrOf(selectedEncounter), selectedEncounter) ?? undefined,
      });
    }
    if (menu === "docs" || menu === "medcert") {
      props.runSequence({
        sequence: DOCUMENT_V4_SEQUENCE,
        action: DOCUMENT_ACTIONS.LOAD,
        card: DOCUMENT_V4_CARD,
        restart: true,
        encounterId: asNumber(selectedEncounter?.id),
        patientId: asNumber(selectedEncounter?.patient_id),
        emrId: emrIdOf(emrOf(selectedEncounter), selectedEncounter) ?? undefined,
        doctorId: doctorIdOf(selectedEncounter, emrOf(selectedEncounter)),
        patientHn: String(selectedEncounter?.hn || ""),
      });
    }
    if (menu === "screen") {
      props.runSequence({
        sequence: SCREEN_V4_SEQUENCE,
        action: SCREEN_ACTIONS.LOAD,
        card: SCREEN_V4_CARD,
        restart: true,
        encounterId: asNumber(selectedEncounter?.id),
        patientId: asNumber(selectedEncounter?.patient_id),
        doctorId: doctorIdOf(selectedEncounter, emrOf(selectedEncounter)),
        patientHn: String(selectedEncounter?.hn || ""),
      });
    }
  };

  const handleSelectEncounter = (encounter: unknown): void => openEncounter(encounter, false);

  const handleBackToQueue = (): void => {
    setSelectedEncounter(null);
    setPatientMenu("doctor");
    setActiveNav("queue");
  };

  const handleActionEvent = (action: string, encounter: unknown): void => {
    const stage = stageOf(encounter);
    if (stage?.opensEncounter) {
      /* คิวของขั้น "รอพบแพทย์" ปุ่มท้ายแถวคือ เรียกเข้าตรวจ
         ทำ IN_QUEUE -> IN_EXAM ให้เลย (V3: CardPatientListInterface.ts:1904-1912)
         ids ไม่ครบก็ไม่เป็นไร ExamV4.StartExam resolve emr + doctor เองจาก encounter */
      openEncounter(
        encounter,
        stage.key === CALL_IN_STAGE_KEY && callInAllowedFor(encounter as { allowed_actions?: unknown }),
      );
    }

    /* คิวของขั้น "กำลังพบแพทย์" ปุ่ม "ส่งต่อ": อยู่หน้าคิว ไม่เปิด panel แต่เปลี่ยนสถานะ
       ไปขั้นถัดไปตามคำสั่งที่เหลือ (PatientListV4.NEXT_STAGE -> EncounterActionOnly patch
       + ค้นหาใหม่ + บันทึก audit ผู้ทำ/เวลา ดู sequence/PatientListV4.ts) */
    if (stage?.key === "in-doctor") {
      const record: EncounterRecord = isEncounterRecord(encounter) ? encounter : {};
      props.runSequence({
        sequence: PATIENT_LIST_V4_SEQUENCE,
        action: PATIENT_LIST_V4_ACTIONS.NEXT_STAGE,
        card: PATIENT_LIST_V4_CARD,
        encounterId: asNumber(record.id),
        patientName: String(record.patient_name ?? ""),
        doctorOrders: (record.doctor_orders ?? []) as {
          specific_type?: string;
          order_status?: string;
        }[],
        actorId: asNumber(django?.user?.id) ?? null,
        actorName: asText(django?.user?.full_name) || asText(userProfile?.name) || null,
      });
    }

    /* ขั้น "รอเก็บสิ่งส่งตรวจ": ปุ่มพาไปหน้า Specimen Management (Services Queue /
     phlebotomy ของ V3 ที่ MainHIS ต่อไว้ผ่าน v3Pages.servicesQueue) */
    if (stage?.key === "wait-lab") {
      setActiveNav("dashboard");
      setDashboardTab("services");
    }

    onEvent?.({
      message: "PatientNextAction",
      params: { action, encounter },
    });
  };

  const handleOpenPatient = (target: OpenPatientTarget, patientId?: number): void => {
    setMemberHistoryPatientId(target === "history" ? patientId : undefined);
    setPosMenu(POS_MENU);
    setMemberMenu(target);
  };

  const memberContent = (menu: RegistrationMenuKey): React.ReactNode => {
    if (menu === "info") {
      return props.v3Pages?.patientInfo ? (
        <div className="hisv4-v3-page">{props.v3Pages.patientInfo()}</div>
      ) : (
        <div className="hv4-placeholder">ยังไม่ได้ต่อหน้า ข้อมูลผู้รับบริการ</div>
      );
    }
    if (memberPatientId === undefined) {
      return <div className="hv4-placeholder">กำลังโหลดข้อมูลผู้ป่วย</div>;
    }
    if (menu === "history") {
      const selected =
        asNumber(props.SetPatientSequence?.patient?.id) === memberPatientId
          ? props.SetPatientSequence?.patient
          : undefined;
      return (
        <CardPatientHistoryV4
          key={memberPatientId}
          apiToken={props.controller?.apiToken ?? undefined}
          patient={{ hn: asText(selected?.hn), name: asText(selected?.full_name) }}
          ids={{ patientId: memberPatientId }}
          history={props.PatientHistoryV4Sequence}
          runSequence={props.runSequence}
          buttonLoadCheck={props.buttonLoadCheck}
          errorMessage={props.errorMessage}
          setProp={props.setProp}
          divisionOptions={(props.divisionOptions ?? [])
            .map((option: any) => ({
              id: asNumber(option?.id),
              label: asText(option?.name ?? option?.label ?? option?.code),
            }))
            .filter((option: { label: string }) => option.label !== "")}
        />
      );
    }
    if (menu === "pos") {
      const error = props.errorMessage?.[REGISTRATION_BTN_ACTS.LOAD_CUSTOMER];
      if (!memberCustomerReady) {
        return error ? (
          <AlertV4 tone="warn" error={error} />
        ) : (
          <div className="hv4-placeholder">กำลังโหลดข้อมูลลูกค้า</div>
        );
      }
      return props.v3Pages?.mePOSMenu ? (
        <div className="hisv4-v3-page">{props.v3Pages.mePOSMenu(posMenu, setPosMenu)}</div>
      ) : (
        <div className="hv4-placeholder">ยังไม่ได้ต่อหน้า POS</div>
      );
    }
    /* CreateUpdateEncounter อ่านผู้ป่วยจาก state.selectedPatient */
    if (asNumber(props.selectedPatient?.id) !== memberPatientId) {
      return <div className="hv4-placeholder">กำลังโหลดข้อมูลผู้ป่วย</div>;
    }
    return (
      <CardOpenEncounterV4
        key={props.selectedDivision?.id}
        apiToken={props.controller?.apiToken ?? ""}
        divisionOptions={props.divisionOptions}
        selectedDivisionId={asNumber(props.selectedDivision?.id)}
        patientId={memberPatientId}
        walkInOptions={props.RegistrationV4Sequence?.walkIn}
        CreateUpdateEncounterSequence={props.CreateUpdateEncounterSequence}
        appointmentEncounterList={props.appointmentEncounterList}
        selectedAppointmentItems={props.selectedAppointmentItems}
        rehabAppointmentDoctors={props.rehabAppointmentDoctors}
        masterOptions={props.masterOptions}
        buttonLoadCheck={props.buttonLoadCheck}
        errorMessage={props.errorMessage}
        successMessage={props.successMessage}
        userName={asText(django?.user?.full_name) || asText(props.userProfile?.name)}
        runSequence={props.runSequence}
        setProp={(key, value) => setProp?.(key, value)}
      />
    );
  };

  /** หน้าของ V3 ที่ MainHIS ส่งมา (ไม่มี = ยังไม่ได้ต่อ) */
  const v3Page = (page: ((arg?: string) => React.ReactNode) | undefined, title: string) =>
    page ? (
      <div className="hisv4-v3-page">{page()}</div>
    ) : (
      <div className="hv4-placeholder">ยังไม่ได้ต่อหน้า {title}</div>
    );

  // เนื้อหาแท็บสรุปค่าใช้จ่าย
  const renderBillingTab = (key: BillingTabKey) => {
    if (key === "wallet") {
      return (
        <BillingWalletTabV4
          BillingWalletV4Sequence={props.BillingWalletV4Sequence}
          patientId={asNumber(selectedEncounter?.patient_id) ?? undefined}
          patient={patient}
          buttonLoadCheck={props.buttonLoadCheck}
          errorMessage={props.errorMessage}
          runSequence={props.runSequence}
          setProp={props.setProp}
        />
      );
    }
    if (key === "pending") {
      return (
        <BillingPendingTabV4
          BillingV4Sequence={props.BillingV4Sequence}
          patientId={asNumber(selectedEncounter?.patient_id) ?? undefined}
          patient={patient}
          masterOptions={props.masterOptions}
          selectedDivision={props.selectedDivision}
          buttonLoadCheck={props.buttonLoadCheck}
          errorMessage={props.errorMessage}
          runSequence={props.runSequence}
          setProp={props.setProp}
          onGoOrders={() => handleSelectMenu("orders")}
        />
      );
    }
    return (
      <BillingHistoryTabV4
        BillingHistoryV4Sequence={props.BillingHistoryV4Sequence}
        patientId={asNumber(selectedEncounter?.patient_id) ?? undefined}
        masterOptions={props.masterOptions}
        buttonLoadCheck={props.buttonLoadCheck}
        errorMessage={props.errorMessage}
        runSequence={props.runSequence}
        setProp={props.setProp}
      />
    );
  };

  /* ---------------------------------------------------------------
     #86727: Communication Note (ปุ่มในคอลัมน์ HN + pop-up ของตารางคิว)
     Note = DPO sensitive_note ผูกกับ EMR (medical_record) ต่อ encounter —
     PatientListV4 อ่าน (LOAD_NOTES) ตอนท้าย SEARCH และ group ไว้ที่
     slice.notesByEmr -> จำนวนบนแถว = จำนวน record ของ EMR แถวนั้น
     (endpoint เดียวกับ LOAD_SENSITIVE_NOTE ของหน้าตรวจรักษา V4)
     --------------------------------------------------------------- */
  const communicationNotesOf = (row: QueueEncounter) => {
    const emr = row.medical_record;
    return emr === undefined ? [] : (props.PatientListV4Sequence?.notesByEmr?.[emr] ?? []);
  };
  const communicationNoteCountOf = (row: QueueEncounter): number =>
    communicationNotesOf(row).filter((note) => note.note !== "").length;
  const openCommunicationNote = (row: QueueEncounter): void => {
    setNoteModalRow(row);
  };
  const closeCommunicationNote = (): void => {
    setNoteModalRow(null);
  };
  const savingNote = Boolean(props.PatientListV4Sequence?.notesSaving);
  const saveCommunicationNote = (text: string): void => {
    const emrId = noteModalRow?.medical_record;
    if (emrId === undefined || text === "" || savingNote) return;
    /* SAVE_NOTE ใน sequence = สร้าง record ใหม่ (POST) แล้ว LOAD_NOTES ใหม่ -> จำนวนบนแถว update ทันที */
    props.runSequence({
      sequence: PATIENT_LIST_V4_SEQUENCE,
      action: PATIENT_LIST_V4_ACTIONS.SAVE_NOTE,
      card: PATIENT_LIST_V4_CARD,
      emrId,
      note: text,
    });
  };

  const queueTabs = (
    <TabsV4<QueueTab>
      ariaLabel="คิววันนี้"
      items={QUEUE_TABS}
      active={queueTab}
      onChange={setQueueTab}
    />
  );

  return (
    <div className="hisv4-container">
      {/* Top Header Navigation Bar */}
      <TopBarV4
        {...props}
        mode={props.mode || "hisv4"}
        django={django}
        userProfile={userProfile}
        setProp={setProp}
        onEvent={onEvent}
        selectedLanguage={selectedLanguage}
        onChangeSelectedLanguage={onChangeSelectedLanguage}
        onBackToMainHIS={onBackToMainHIS || (() => setProp?.("mode", "patient"))}
      />

      {/* Main Body */}
      <div className="hisv4-body-layout">
        {/* Left Sidebar */}
        <aside className="hisv4-sidebar">
          <div className="hisv4-sidebar-top">
            {RAIL.map(({ key, label, Icon }) => (
              <button
                key={key}
                className={`hisv4-sidebar-item ${activeNav === key ? "active" : ""}`}
                onClick={() => setActiveNav(key)}
                title={label}
              >
                <Icon size={20} />
                <span>{label}</span>
              </button>
            ))}
          </div>

          <div className="hisv4-sidebar-bottom">
            <button
              className="hisv4-sidebar-item back-btn"
              onClick={() => {
                if (onBackToMainHIS) {
                  onBackToMainHIS();
                } else {
                  setProp?.("mode", "patient");
                }
              }}
              title="กลับไปหน้าหลัก MainHIS (V3)"
            >
              <Undo2 size={20} />
              <span style={{ fontSize: "0.65rem", textAlign: "center" }}>กลับ V3</span>
            </button>
          </div>
        </aside>

        {/* Content Area */}
        <main
          className={`hisv4-content-area${
            (activeNav === "queue" && queueTab !== "orders" && selectedEncounter !== null) ||
            activeNav === "chat" ||
            (activeNav === "patient" && patientTab === "member" && memberMenu !== null)
              ? " hisv4-content-area--flush"
              : ""
          }`}
        >
          {activeNav === "queue" && queueTab === "orders" ? (
            <CardPurchaseOrdersV4
              tabs={queueTabs}
              divisionId={asNumber(props.selectedDivision?.id)}
              orderSummary={props.MeBillingSequence?.orderSummary}
              runSequence={props.runSequence}
            />
          ) : activeNav === "patient" && patientTab === "member" && memberMenu !== null ? (
            <ViewRegistrationV4
              patient={
                memberMenu === "history" &&
                asNumber(props.SetPatientSequence?.patient?.id) !== memberPatientId
                  ? undefined
                  : props.SetPatientSequence?.patient
              }
              tier={
                memberPatientId === undefined
                  ? undefined
                  : props.RegistrationV4Sequence?.tiers?.[memberPatientId]
              }
              latestChannel={memberWalkIn?.latestChannel}
              specialRequests={memberWalkIn?.specialRequests}
              activeMenu={memberMenu}
              onSelectMenu={setMemberMenu}
              onBack={() => setMemberMenu(null)}
            >
              {memberContent(memberMenu)}
            </ViewRegistrationV4>
          ) : activeNav === "appointment" ? (
            <CardAppointmentV4
              key={props.selectedDivision?.id}
              apiToken={props.controller?.apiToken ?? ""}
              divisionId={asNumber(props.selectedDivision?.id)}
              runSequence={props.runSequence}
              setProp={setProp}
              onEvent={onEvent}
              AppointmentListMEHSequence={props.AppointmentListMEHSequence}
              MakeAppointmentMeHeySequence={props.MakeAppointmentMeHeySequence}
              searchedItemListWithKey={props.searchedItemListWithKey}
              buttonLoadCheck={props.buttonLoadCheck}
              errorMessage={props.errorMessage}
            />
          ) : activeNav === "chat" && props.chatProps ? (
            <CardChatV4
              ref={props.chatRef}
              {...props.chatProps}
              onGoAppointments={() => setActiveNav("appointment")}
            />
          ) : activeNav === "patient" ? (
            <>
              <TabsV4<PatientTab>
                ariaLabel="Patient"
                items={PATIENT_TABS}
                active={patientTab}
                onChange={setPatientTab}
              />
              {patientTab === "queue" ? (
                v3Page(props.v3Pages?.mePOSQueue, "Patient Queue")
              ) : (
                <CardSearchMemberV4
                  SetPatientSequence={props.SetPatientSequence}
                  RegistrationV4Sequence={props.RegistrationV4Sequence}
                  buttonLoadCheck={props.buttonLoadCheck}
                  errorMessage={props.errorMessage}
                  successMessage={props.successMessage}
                  runSequence={props.runSequence}
                  setProp={(key, value) => setProp?.(key, value)}
                  onOpenPatient={handleOpenPatient}
                />
              )}
            </>
          ) : activeNav === "dashboard" ? (
            <>
              <TabsV4<DashboardTab>
                ariaLabel="Dashboard"
                items={DASHBOARD_TABS}
                active={dashboardTab}
                onChange={setDashboardTab}
              />
              {dashboardTab === "services"
                ? v3Page(props.v3Pages?.servicesQueue, "Services Queue")
                : v3Page(props.v3Pages?.report, "Report")}
            </>
          ) : activeNav === "queue" ? (
            <ViewEncounterV4
              selectedEncounter={selectedEncounter}
              onSelectEncounter={handleSelectEncounter}
              onActionEvent={handleActionEvent}
              onEvent={onEvent}
              PatientListV4Sequence={props.PatientListV4Sequence}
              searchedItemListWithKey={props.searchedItemListWithKey}
              divisionOptions={props.divisionOptions}
              division={props.selectedDivision?.id}
              onBackToQueue={handleBackToQueue}
              listView={(context) => (
                <CardPatientListV4
                  apiToken={props.controller?.apiToken ?? ""}
                  onSelectEncounter={context.onSelectEncounter}
                  onActionEvent={context.onActionEvent}
                  onEvent={onEvent}
                  runSequence={props.runSequence}
                  buttonLoadCheck={props.buttonLoadCheck}
                  errorMessage={props.errorMessage}
                  setProp={props.setProp}
                  PatientListV4Sequence={props.PatientListV4Sequence}
                  searchedItemListWithKey={props.searchedItemListWithKey}
                  divisionOptions={props.divisionOptions}
                  division={props.selectedDivision?.id}
                  savedFilter={queueFilter}
                  onFilterChange={setQueueFilter}
                  tabs={queueTabs}
                  communicationNote={{
                    countOf: communicationNoteCountOf,
                    onOpen: openCommunicationNote,
                  }}
                />
              )}
              panelView={() => (
                <PatientPanelV4
                  patient={patient}
                  encounter={encounter}
                  nextAppointment={examView?.panelLoaded ? examView.nextAppointment : undefined}
                  patientADR={patientADRState()}
                  problemListDPO={problemListState()}
                  onEditFlags={examView !== null ? () => setFlagEditorOpen(true) : undefined}
                  member={examView?.panelLoaded ? examView.member : undefined}
                  channels={examView?.channels}
                  latestChannel={examView?.latestChannel}
                  specialRequests={examView?.specialRequests}
                  teamMessageErrors={examView?.teamMessageErrors}
                  medications={examView?.medications}
                  coverage={examView?.coverage}
                  activeMenu={patientMenu}
                  onSelectMenu={handleSelectMenu}
                  onBackToQueue={handleBackToQueue}
                  presence={presenceBinding}
                  checkout={{
                    encounter: examView?.checkout,
                    saving:
                      props.buttonLoadCheck?.[
                        `${EXAM_V4_CARDS.checkout}_${EXAM_ACTIONS.CHECK_OUT}`
                      ] === BLC_STATUS.LOADING ||
                      props.buttonLoadCheck?.[
                        `${EXAM_V4_CARDS.checkout}_${EXAM_ACTIONS.CANCEL_CHECK_OUT}`
                      ] === BLC_STATUS.LOADING,
                    error: props.errorMessage?.[EXAM_V4_CARDS.checkout],
                    onCheckout: (checkoutCause, note, deathDatetime, onDone) =>
                      props.runSequence({
                        sequence: EXAM_SEQUENCE,
                        action: EXAM_ACTIONS.CHECK_OUT,
                        card: EXAM_V4_CARDS.checkout,
                        ...examIds(),
                        checkoutCause,
                        note,
                        deathDatetime,
                        onDone,
                      }),
                    onCancel: (note, onDone) =>
                      props.runSequence({
                        sequence: EXAM_SEQUENCE,
                        action: EXAM_ACTIONS.CANCEL_CHECK_OUT,
                        card: EXAM_V4_CARDS.checkout,
                        ...examIds(),
                        note,
                        onDone,
                      }),
                  }}
                >
                  {patientMenu === "doctor" ? (
                    <CardDoctorV4
                      doctorName={emrOf(selectedEncounter)?.doctor_name}
                      currentUserId={django?.user?.id}
                      onSelectMenu={handleSelectMenu}
                      onBackToQueue={handleBackToQueue}
                      exam={props.ExamV4Sequence}
                      ids={examIds()}
                      runSequence={props.runSequence}
                      buttonLoadCheck={props.buttonLoadCheck}
                      errorMessage={props.errorMessage}
                      setProp={props.setProp}
                      encounterStatus={encounterStatusOf().status}
                      encounterStatusName={encounterStatusOf().statusName}
                      canCallInExam={canCallInExam()}
                    />
                  ) : patientMenu === "gallery" ? (
                    <CardGalleryV4
                      encounterNumber={encounter?.number ?? ""}
                      doctorName={emrOf(selectedEncounter)?.doctor_name}
                      onSelectMenu={handleSelectMenu}
                      exam={props.ExamV4Sequence}
                      runSequence={props.runSequence}
                      buttonLoadCheck={props.buttonLoadCheck}
                      errorMessage={props.errorMessage}
                      setProp={props.setProp}
                    />
                  ) : patientMenu === "nursing" ? (
                    <CardNursingV4
                      onSelectMenu={handleSelectMenu}
                      exam={props.ExamV4Sequence}
                      consult={props.MultidisciplinaryV4Sequence}
                      currentUsername={asText(props.userProfile?.username)}
                      currentUserId={
                        asNumber(props.django?.user?.id) ??
                        asNumber(props.currentDoctor?.id) ??
                        undefined
                      }
                      doctorOrders={nurseDoctorOrders()}
                      runSequence={props.runSequence}
                      buttonLoadCheck={props.buttonLoadCheck}
                      errorMessage={props.errorMessage}
                      setProp={props.setProp}
                    />
                  ) : patientMenu === "orders" && isLoaded ? (
                    <CardOrdersV4
                      key={`${props.selectedDivision?.id}:${selectedEncounter.id}`}
                      apiToken={props.controller?.apiToken ?? ""}
                      divisionId={asNumber(props.selectedDivision?.id)}
                      setProp={props.setProp}
                      masterOptions={props.masterOptions}
                      runSequence={props.runSequence}
                      PatientOrdersV4Sequence={props.PatientOrdersV4Sequence}
                      OrderCentralLabSequence={props.OrderCentralLabSequence}
                      OrderSetTemplateSequence={props.OrderSetTemplateSequence}
                      OrderSheetV4Sequence={props.OrderSheetV4Sequence}
                      OrderAppointmentV4Sequence={props.OrderAppointmentV4Sequence}
                      doctorId={props.currentDoctor?.id}
                      emrId={emrOf(selectedEncounter)?.id}
                      isDoctor={isDoctorRole(django?.user?.role_types)}
                      encounter={{
                        id: Number(selectedEncounter.id),
                        hn: String(selectedEncounter.hn),
                        created: String(selectedEncounter.created),
                        doctor_orders: selectedEncounter.doctor_orders as any[],
                      }}
                      encounterNumber={encounter?.number ?? ""}
                      patient={{
                        hn: String(selectedEncounter.hn),
                        name: patient?.full_name ?? "",
                        phone: patient?.main_contact_phone_no || patient?.main_home_phone_no || "",
                      }}
                      divisionName={props.selectedDivision?.name ?? ""}
                      onGoAppointments={() => setActiveNav("appointment")}
                      onAppointmentChanged={() =>
                        props.runSequence({
                          sequence: EXAM_SEQUENCE,
                          action: EXAM_ACTIONS.LOAD_PANEL,
                          card: EXAM_V4_CARDS.panel,
                          ...examIds(),
                        })
                      }
                      buttonLoadCheck={props.buttonLoadCheck}
                      errorMessage={props.errorMessage}
                      searchedItemListWithKey={props.searchedItemListWithKey}
                      onEvent={onEvent}
                    />
                  ) : patientMenu === "history" ? (
                    <CardPatientHistoryV4
                      apiToken={props.controller?.apiToken ?? undefined}
                      patient={{
                        hn: String(selectedEncounter?.hn ?? ""),
                        name: patient?.full_name ?? "",
                      }}
                      history={props.PatientHistoryV4Sequence}
                      ids={{
                        patientId: asNumber(selectedEncounter?.patient_id),
                        encounterId: asNumber(selectedEncounter?.id),
                        emrId: emrIdOf(emrOf(selectedEncounter), selectedEncounter) ?? undefined,
                      }}
                      runSequence={props.runSequence}
                      buttonLoadCheck={props.buttonLoadCheck}
                      errorMessage={props.errorMessage}
                      setProp={props.setProp}
                      divisionOptions={(props.divisionOptions ?? [])
                        .map((option: any) => ({
                          id: asNumber(option?.id),
                          label: asText(option?.name ?? option?.label ?? option?.code),
                        }))
                        .filter((option: { label: string }) => option.label !== "")}
                      currentDivisionName={asText(props.selectedDivision?.name)}
                      onSelectMenu={handleSelectMenu}
                      onBackToQueue={handleBackToQueue}
                    />
                  ) : patientMenu === "results" ? (
                    <CardResultSummaryV4
                      patient={patient}
                      encounter={{
                        id: Number(selectedEncounter?.id),
                        number: encounter?.number,
                        hn: String(selectedEncounter?.hn || ""),
                      }}
                      results={props.ResultSummaryV4Sequence}
                      ids={{
                        encounterId: asNumber(selectedEncounter?.id),
                        patientId: asNumber(selectedEncounter?.patient_id),
                        emrId: emrIdOf(emrOf(selectedEncounter), selectedEncounter) ?? undefined,
                      }}
                      runSequence={props.runSequence}
                      buttonLoadCheck={props.buttonLoadCheck}
                      errorMessage={props.errorMessage}
                      currentDoctor={props.currentDoctor}
                      currentUsername={
                        asText(props.userProfile?.username) || asText(props.currentDoctor?.username)
                      }
                      setProp={props.setProp}
                      onSelectMenu={handleSelectMenu}
                      onBackToQueue={handleBackToQueue}
                    />
                  ) : patientMenu === "docs" || patientMenu === "medcert" ? (
                    <CardDocumentV4
                      key={asNumber(selectedEncounter?.id) ?? "no-encounter"}
                      patient={patient}
                      encounter={{
                        id: Number(selectedEncounter?.id),
                        number: encounter?.number,
                        hn: String(selectedEncounter?.hn || ""),
                        doctor_name: encounter?.doctor_name ?? undefined,
                      }}
                      canIssueCert={isDoctorUser()}
                      initialDocMode={patientMenu === "medcert" ? "medcert" : "document"}
                      docs={props.DocumentV4Sequence}
                      divisionOptions={props.divisionOptions}
                      printListController={props.printListController}
                      masterOptions={props.masterOptions}
                      masterData={props.masterData}
                      selectedDivision={props.selectedDivision}
                      ids={{
                        encounterId: asNumber(selectedEncounter?.id),
                        patientId: asNumber(selectedEncounter?.patient_id),
                        emrId: emrIdOf(emrOf(selectedEncounter), selectedEncounter) ?? undefined,
                        doctorId:
                          doctorIdOf(selectedEncounter, emrOf(selectedEncounter)) ?? undefined,
                        patientHn: String(selectedEncounter?.hn || ""),
                      }}
                      runSequence={props.runSequence}
                      buttonLoadCheck={props.buttonLoadCheck}
                      errorMessage={props.errorMessage}
                      onSelectMenu={handleSelectMenu}
                      onBackToQueue={handleBackToQueue}
                    />
                  ) : patientMenu === "billing" ? (
                    <CardBillingV4
                      encounterNumber={String(encounter?.number ?? "")}
                      renderTab={renderBillingTab}
                    />
                  ) : patientMenu === "screen" ? (
                    <CardScreenV4
                      patient={patient}
                      encounter={{
                        id: Number(selectedEncounter?.id),
                        number: encounter?.number,
                        hn: String(selectedEncounter?.hn || ""),
                      }}
                      screen={props.ScreenV4Sequence}
                      ids={{
                        encounterId: asNumber(selectedEncounter?.id),
                        patientId: asNumber(selectedEncounter?.patient_id),
                        doctorId:
                          doctorIdOf(selectedEncounter, emrOf(selectedEncounter)) ?? undefined,
                        patientHn: String(selectedEncounter?.hn || ""),
                      }}
                      runSequence={props.runSequence}
                      buttonLoadCheck={props.buttonLoadCheck}
                      errorMessage={props.errorMessage}
                      /* ยกเลิกการวัดสัญญาณชีพต้องยืนยันผู้ใช้ (backend AuthenticationForm) */
                      currentUsername={asText(props.userProfile?.username)}
                      onSelectMenu={handleSelectMenu}
                      onBackToQueue={handleBackToQueue}
                    />
                  ) : patientMenu === "tele" && props.chatProps ? (
                    <CardTeleHealthV4
                      patient={{
                        id: selectedEncounter?.patient_id,
                        name: patient?.full_name ?? "",
                        hn: selectedEncounter?.hn ?? "",
                      }}
                      encounter={selectedEncounter}
                      chatDetail={props.chatDetail}
                      TeleHealthV4Sequence={props.TeleHealthV4Sequence}
                      buttonLoadCheck={props.buttonLoadCheck}
                      examIds={examIds()}
                      chatController={props.chatProps.chatController}
                      classifyController={props.classifyController}
                      diagFormController={props.diagFormController}
                      apiToken={props.chatProps.apiToken}
                      userId={props.chatProps.userId}
                      divisionId={props.selectedDivision?.id}
                      errorMessage={props.errorMessage}
                      runSequence={props.runSequence}
                      setProp={props.setProp}
                      onOpenVideoCall={props.chatProps.onOpenVideoCall}
                      onSelectMenu={handleSelectMenu}
                    />
                  ) : patientMenu === "package" ? (
                    <CardPackageV4
                      patientId={selectedEncounter?.patient_id}
                      PackageV4Sequence={props.PackageV4Sequence}
                      masterOptions={props.masterOptions}
                      buttonLoadCheck={props.buttonLoadCheck}
                      errorMessage={props.errorMessage}
                      runSequence={props.runSequence}
                      setProp={props.setProp}
                      onGoAppointments={() => setActiveNav("appointment")}
                    />
                  ) : patientMenu === "orders" ? (
                    /* แถว "รอจ่ายยา" เปิดเมนูคำสั่ง: parent ยังโหลด selectedEncounter ไม่เสร็จ */
                    <div className="hv4-placeholder">กำลังโหลดข้อมูลคำสั่งของ encounter นี้</div>
                  ) : (
                    <div className="hv4-placeholder">
                      กำลังพัฒนาหน้า {patientMenuTitle(patientMenu)} ตามแบบของ design
                    </div>
                  )}

                  {examView !== null && flagEditorOpen && (
                    <CardPatientFlagV4
                      exam={props.ExamV4Sequence}
                      patientId={examIds().patientId}
                      runSequence={props.runSequence}
                      buttonLoadCheck={props.buttonLoadCheck}
                      errorMessage={props.errorMessage}
                      setProp={props.setProp}
                      onClose={() => setFlagEditorOpen(false)}
                    />
                  )}
                </PatientPanelV4>
              )}
            />
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "var(--hv4-ink-off)",
                fontSize: "1.1rem",
              }}
            >
              กำลังพัฒนาหน้า {activeNav}
            </div>
          )}
        </main>
      </div>

      {/* #86727: pop-up Communication Note ของ encounter นี้ (modal รวมใน
          CommunicationNoteModalV4 — หน้าอื่นนำไปใช้ซ้ำได้)
          กดจากปุ่มในคอลัมน์ HN: ไม่เปิดหน้าผู้ป่วย/ไม่เปลี่ยนแถวที่เลือก */}
      {noteModalRow && (
        <CommunicationNoteModalV4
          encounter={noteModalRow}
          notes={communicationNotesOf(noteModalRow)}
          error={props.PatientListV4Sequence?.notesError ?? null}
          saving={savingNote}
          onClose={closeCommunicationNote}
          onSave={saveCommunicationNote}
        />
      )}
    </div>
  );
};

export default MainHISV4;
